import { useState } from "react";
import Button from "./Button";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

function DealsSearch({ onSearch }) {
  const { t } = useTranslation();

  const initialFormData = {
    mortgageAccountNumber: "",
    poS_System_ID: "",
    primaryBorrowerName: "",
    propertyAddress: "",
    
    applicationDateFrom: null,
    applicationDateTo: null,
    closingDateFrom: null,
    closingDateTo: null,
    fundingDateFrom: null,
    fundingDateTo: null,
    loanStatus: null,
    
    loanSubStatus: "",
    loanPurpose: "",
    loanSubPurpose: "",
    lob: "",
    portalStatus: ""
  };

  const [formData, setFormData] = useState(initialFormData);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
  };

  const validateDateRange = (fromDate, toDate) => {
    if (fromDate && toDate) {
      return dayjs(fromDate).isBefore(dayjs(toDate)) || dayjs(fromDate).isSame(dayjs(toDate));
    }
    return true;
  };

  const processDateValue = (value) => {
    return value ? value : null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (Object.values(formData).every((value) => value === "")) {
      return;
    }

    if (!validateDateRange(formData.applicationDateFrom, formData.applicationDateTo)) {
      alert(t("dealsSearch.invalidApplicationDateRange"));
      return;
    }

    if (!validateDateRange(formData.closingDateFrom, formData.closingDateTo)) {
      alert(t("dealsSearch.invalidClosingDateRange"));
      return;
    }

    // Process the form data
    const processedFormData = {
      ...initialFormData,  // Start with all fields
      ...formData,  // Overwrite with user input
      applicationDateFrom: processDateValue(formData.applicationDateFrom),
      applicationDateTo: processDateValue(formData.applicationDateTo),
      closingDateFrom: processDateValue(formData.closingDateFrom),
      closingDateTo: processDateValue(formData.closingDateTo),
      fundingDateFrom: processDateValue(formData.fundingDateFrom),
      fundingDateTo: processDateValue(formData.fundingDateTo),
    };

    onSearch(processedFormData);
  };

  const handleClear = () => {
    // Clear UI first with empty strings
    const clearedFormData = {
      ...initialFormData,
      applicationDateFrom: "",
      applicationDateTo: "",
      closingDateFrom: "",
      closingDateTo: "",
      fundingDateFrom: "",
      fundingDateTo: ""
    };
    setFormData(clearedFormData);
  
    // Then set dates to null for API
    setTimeout(() => {
      setFormData({
        ...clearedFormData,
        applicationDateFrom: null,
        applicationDateTo: null,
        closingDateFrom: null,
        closingDateTo: null,
        fundingDateFrom: null,
        fundingDateTo: null
      });
    }, 0);
    
    onSearch(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const hasData = Object.values(formData).some((value) => value !== "");
      if (hasData) {
        handleSubmit(e);
      }
    }
  };

  return (
    <div className="w-full p-5">
      <div className="flex flex-col gap-2">
        <span className="labels text-xl font-semibold">
          {t("dealsSearch.DealsSearch")}
        </span>
        <div className="rounded-md border-2 p-5">
          <form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
            <div className="w-full flex p-5 flex-col md:flex-row lg:flex-row justify-between items-center gap-5">
              <div className="w-full md:w-1/2 flex flex-col gap-2">
                <InputField
                  id="mortgageAccountNumber"
                  label={t("dealsSearch.MortgageAccountNumber")}
                  value={formData.mortgageAccountNumber}
                  onChange={handleChange}
                  inputWidth="min-w-56"
                  type="text"
                />
                <InputField
                  id="poS_System_ID"
                  label={t("dealsSearch.BrokerAccountID")}
                  value={formData.poS_System_ID}
                  onChange={handleChange}
                  inputWidth="min-w-56"
                />
                <InputField
                  id="lob"
                  label={t("dealsSearch.LOB")}
                  value={formData.lob}
                  onChange={handleChange}
                  inputWidth="min-w-56"
                />
                <InputField
                  id="primaryBorrowerName"
                  label={t("dealsSearch.PrimaryBorrowerName")}
                  value={formData.primaryBorrowerName}
                  onChange={handleChange}
                  inputWidth="min-w-56"
                />
              </div>
              <div className="w-full md:w-1/2 flex flex-col gap-2">
                <InputField
                  id="propertyAddress"
                  label={t("dealsSearch.SecuredProperty")}
                  value={formData.propertyAddress}
                  onChange={handleChange}
                  inputWidth="min-w-56 lg:min-w-96"
                />
                <DateRangeFields
                  fromId="applicationDateFrom"
                  toId="applicationDateTo"
                  label={t("dealsSearch.ApplicationDateRange")}
                  fromLabel={t("dealsSearch.fromInput")}
                  toLabel={t("dealsSearch.toInput")}
                  fromValue={formData.applicationDateFrom}
                  toValue={formData.applicationDateTo}
                  onChange={handleChange}
                />
                <DateRangeFields
                  fromId="closingDateFrom"
                  toId="closingDateTo"
                  label={t("dealsSearch.ClosingDateRange")}
                  fromLabel={t("dealsSearch.fromInput")}
                  toLabel={t("dealsSearch.toInput")}
                  fromValue={formData.closingDateFrom}
                  toValue={formData.closingDateTo}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="w-full flex justify-around items-center gap-2 mt-5">
              <button
                type="button"
                onClick={handleClear}
                className="w-full bg-zinc-500"
              >
                {t("dealsSearch.btn.ClearSearch")}
              </button>
              <Button
                variant={"search"}
                type={"submit"}
                label={t("dealsSearch.btn.Search")}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function InputField({ id, label, value, onChange, type = "text", inputWidth = "w-32" }) {
  return (
    <div className="w-full min-w-32 flex flex-col lg:flex-row justify-between items-center md:items-start gap-2">
      <label className="labels font-semibold" htmlFor={id}>
        {label}
      </label>
      <input
        className={`max-w-fit ${inputWidth}`}
        value={value}
        onChange={onChange}
        autoComplete="off"
        id={id}
        type={type}
      />
    </div>
  );
}


function DateRangeFields({ fromId, toId, label, fromLabel, fromValue, toLabel, toValue, onChange }) {

  return (
    <div className="w-full flex flex-col gap-2">
      <label className="labels font-semibold">{label}</label>
      <div className="flex flex-col w-full lg:flex-row justify-between gap-2">
        <div className="flex felx-col gap-2 justify-end">  
          <span className="text-sm font-bold italic w-24 lg:w-fit text-gray-400">{fromLabel}</span>
          <input
            className="lg:max-w-fit"
            value={fromValue}
            onChange={onChange}
            autoComplete="off"
            id={fromId}
            type="date"
            placeholder="From"
          />
        </div>
        <div className="flex felx-col gap-2 justify-end">
          <span className="text-sm font-bold italic w-24 lg:w-fit text-gray-400">{toLabel}</span>
          <input
            className="lg:max-w-fit"
            value={toValue}
            onChange={onChange}
            autoComplete="off"
            id={toId}
            type="date"
            placeholder="To"
          />
        </div>
      </div>
    </div>
  );
}

InputField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  type: PropTypes.string,
  inputWidth: PropTypes.string
};

DateRangeFields.propTypes = {
  fromId: PropTypes.string.isRequired,
  toId: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  fromLabel: PropTypes.string.isRequired,
  toLabel: PropTypes.string.isRequired,
  fromValue: PropTypes.string,
  toValue: PropTypes.string,
  onChange: PropTypes.func,
};

DealsSearch.propTypes = {
  onSearch: PropTypes.func.isRequired,
};

export default DealsSearch;